import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';

declare const $: any;

type FooterColKey = 'col1' | 'col2' | 'col3' | 'col4';
type FooterFieldKey = 'title' | 'subtitle';

type CheckoutFormShape = {
  cartTitle: string;
  itemListTitle: string;
  checkoutSideTitle: string;
  shippingEnabled: boolean;
  footerEnabled: boolean;
  footerCols: {
    col1: { title: string; subtitle: string };
    col2: { title: string; subtitle: string };
    col3: { title: string; subtitle: string };
    col4: { title: string; subtitle: string };
  };
};

type CheckoutTranslationField = {
  value: string;
  manual: boolean;
};

type CheckoutFooterTranslationColumn = {
  title: CheckoutTranslationField;
  subtitle: CheckoutTranslationField;
};

type CheckoutTranslationValue = {
  cartTitle: CheckoutTranslationField;
  itemListTitle: CheckoutTranslationField;
  checkoutSideTitle: CheckoutTranslationField;
  footerCols: {
    col1: CheckoutFooterTranslationColumn;
    col2: CheckoutFooterTranslationColumn;
    col3: CheckoutFooterTranslationColumn;
    col4: CheckoutFooterTranslationColumn;
  };
};

type CheckoutTranslatedContentPayload = {
  en: CheckoutTranslationValue;
};

type CheckoutTranslationFieldKey =
  | 'cartTitle'
  | 'itemListTitle'
  | 'checkoutSideTitle'
  | 'footerCols.col1.title'
  | 'footerCols.col1.subtitle'
  | 'footerCols.col2.title'
  | 'footerCols.col2.subtitle'
  | 'footerCols.col3.title'
  | 'footerCols.col3.subtitle'
  | 'footerCols.col4.title'
  | 'footerCols.col4.subtitle';

const FOOTER_COLUMN_KEYS: FooterColKey[] = ['col1', 'col2', 'col3', 'col4'];
const CHECKOUT_TRANSLATION_FIELD_KEYS: CheckoutTranslationFieldKey[] = [
  'cartTitle',
  'itemListTitle',
  'checkoutSideTitle',
  'footerCols.col1.title',
  'footerCols.col1.subtitle',
  'footerCols.col2.title',
  'footerCols.col2.subtitle',
  'footerCols.col3.title',
  'footerCols.col3.subtitle',
  'footerCols.col4.title',
  'footerCols.col4.subtitle',
];

export const CHECKOUT_DEFAULTS: CheckoutFormShape = {
  cartTitle: 'Carrito de Compras',
  itemListTitle: 'Lista de artículos',
  checkoutSideTitle: 'Finalizar la compra',
  shippingEnabled: true,
  footerEnabled: true,
  footerCols: {
    col1: { title: 'Envíos a todo el país', subtitle: '' },
    col2: { title: 'Pago seguro', subtitle: '100% pago seguro' },
    col3: { title: 'Garantía de devolución', subtitle: 'Si tiene algún problema con el artículo' },
    col4: { title: 'Soporte', subtitle: 'Te brindamos atención personalizada' },
  }
};

function createTranslationField(value = ''): CheckoutTranslationField {
  return {
    value,
    manual: false,
  };
}

function cloneTranslationField(field?: CheckoutTranslationField | null): CheckoutTranslationField {
  return {
    value: field?.value || '',
    manual: field?.manual === true,
  };
}

function cloneCheckoutTranslationValue(value: CheckoutTranslationValue): CheckoutTranslationValue {
  return {
    cartTitle: cloneTranslationField(value.cartTitle),
    itemListTitle: cloneTranslationField(value.itemListTitle),
    checkoutSideTitle: cloneTranslationField(value.checkoutSideTitle),
    footerCols: {
      col1: {
        title: cloneTranslationField(value.footerCols.col1.title),
        subtitle: cloneTranslationField(value.footerCols.col1.subtitle),
      },
      col2: {
        title: cloneTranslationField(value.footerCols.col2.title),
        subtitle: cloneTranslationField(value.footerCols.col2.subtitle),
      },
      col3: {
        title: cloneTranslationField(value.footerCols.col3.title),
        subtitle: cloneTranslationField(value.footerCols.col3.subtitle),
      },
      col4: {
        title: cloneTranslationField(value.footerCols.col4.title),
        subtitle: cloneTranslationField(value.footerCols.col4.subtitle),
      },
    },
  };
}

function createCheckoutFlagRecord(defaultValue = false): Record<CheckoutTranslationFieldKey, boolean> {
  return {
    cartTitle: defaultValue,
    itemListTitle: defaultValue,
    checkoutSideTitle: defaultValue,
    'footerCols.col1.title': defaultValue,
    'footerCols.col1.subtitle': defaultValue,
    'footerCols.col2.title': defaultValue,
    'footerCols.col2.subtitle': defaultValue,
    'footerCols.col3.title': defaultValue,
    'footerCols.col3.subtitle': defaultValue,
    'footerCols.col4.title': defaultValue,
    'footerCols.col4.subtitle': defaultValue,
  };
}

@Component({
  selector: 'app-checkout-template-editor',
  templateUrl: './checkout-template-editor.component.html',
  styleUrls: ['./checkout-template-editor.component.scss']
})
export class CheckoutTemplateEditorComponent implements OnInit {
  form: FormGroup;
  loading = false;
  showErrors = false;
  selectedLanguage: 'es' | 'en' = 'es';

  CHECKOUT_DEFAULTS = CHECKOUT_DEFAULTS;
  footerColumnKeys = FOOTER_COLUMN_KEYS;

  englishTranslation: CheckoutTranslationValue = this.buildFallbackEnglishTranslation(CHECKOUT_DEFAULTS);
  initialEnglishTranslation: CheckoutTranslationValue = this.buildFallbackEnglishTranslation(CHECKOUT_DEFAULTS);
  persistedEnglishFieldAvailability: Record<CheckoutTranslationFieldKey, boolean> = createCheckoutFlagRecord(false);
  englishFieldEdited: Record<CheckoutTranslationFieldKey, boolean> = createCheckoutFlagRecord(false);

  private dbSnapshot: CheckoutFormShape | null = null;

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private themingService: ThemingService
  ) {
    this.form = this.fb.group({
      useDefaults: [true],
      cartTitle: [CHECKOUT_DEFAULTS.cartTitle, [Validators.required, Validators.maxLength(255)]],
      itemListTitle: [CHECKOUT_DEFAULTS.itemListTitle, [Validators.required, Validators.maxLength(255)]],
      checkoutSideTitle: [CHECKOUT_DEFAULTS.checkoutSideTitle, [Validators.required, Validators.maxLength(255)]],
      shippingEnabled: [CHECKOUT_DEFAULTS.shippingEnabled],
      footerEnabled: [CHECKOUT_DEFAULTS.footerEnabled],
      footerCols: this.fb.group({
        col1: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col1.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col1.subtitle],
        }),
        col2: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col2.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col2.subtitle],
        }),
        col3: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col3.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col3.subtitle],
        }),
        col4: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col4.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col4.subtitle],
        }),
      })
    });

    this.registerSpanishFieldSync();
  }

  ngOnInit(): void {
    this.loading = true;

    this.lambdaService.getCheckoutTemplate(this.themingService.shopId)
      .then((res: any) => {
        const row = Array.isArray(res?.data) ? res.data[0] : res?.data;

        if (row) {
          this.dbSnapshot = this.dbRowToForm(row);
          const useDefaults = row.use_defaults === 1;

          if (useDefaults) {
            this.form.patchValue({ ...CHECKOUT_DEFAULTS, useDefaults: true }, { emitEvent: false });
            this.form.disable({ emitEvent: false });
            this.form.get('useDefaults')?.enable({ emitEvent: false });
          } else {
            this.form.patchValue({ ...this.dbSnapshot, useDefaults: false }, { emitEvent: false });
            this.form.enable({ emitEvent: false });
          }

          this.initializeTranslationState(row.translatedContent ?? null);
        } else {
          this.form.patchValue({ ...CHECKOUT_DEFAULTS, useDefaults: true }, { emitEvent: false });
          this.form.disable({ emitEvent: false });
          this.form.get('useDefaults')?.enable({ emitEvent: false });
          this.initializeTranslationState(null);
        }
      })
      .catch(err => {
        console.error('GET error', err);
        this.initializeTranslationState(null);
      })
      .finally(() => {
        this.syncEnglishTranslationFallback();
        this.loading = false;
      });

    this.form.get('useDefaults')?.valueChanges.subscribe((useDefaults: boolean) => {
      if (useDefaults) {
        this.form.disable({ emitEvent: false });
        this.form.get('useDefaults')?.enable({ emitEvent: false });
      } else {
        const base = this.dbSnapshot ?? CHECKOUT_DEFAULTS;
        this.form.patchValue({
          cartTitle: base.cartTitle,
          itemListTitle: base.itemListTitle,
          checkoutSideTitle: base.checkoutSideTitle,
          shippingEnabled: base.shippingEnabled,
          footerEnabled: base.footerEnabled,
          footerCols: {
            col1: { ...base.footerCols.col1 },
            col2: { ...base.footerCols.col2 },
            col3: { ...base.footerCols.col3 },
            col4: { ...base.footerCols.col4 },
          }
        }, { emitEvent: false });
        this.form.enable({ emitEvent: false });
      }

      this.syncEnglishTranslationFallback();
    });
  }

  get f() { return this.form.controls as any; }

  private registerSpanishFieldSync(): void {
    const spanishFieldPaths = [
      'cartTitle',
      'itemListTitle',
      'checkoutSideTitle',
      'footerCols.col1.title',
      'footerCols.col1.subtitle',
      'footerCols.col2.title',
      'footerCols.col2.subtitle',
      'footerCols.col3.title',
      'footerCols.col3.subtitle',
      'footerCols.col4.title',
      'footerCols.col4.subtitle',
    ];

    spanishFieldPaths.forEach((path) => {
      this.form.get(path)?.valueChanges.subscribe(() => {
        this.syncEnglishTranslationFallback();
      });
    });
  }

  private dbRowToForm(row: any): CheckoutFormShape {
    return {
      cartTitle: row.cart_title ?? '',
      itemListTitle: row.item_list_title ?? '',
      checkoutSideTitle: row.checkout_title ?? '',
      shippingEnabled: !!row.shipping_enabled,
      footerEnabled: !!row.footer_enabled,
      footerCols: {
        col1: { title: row.footer_col1_title ?? '', subtitle: row.footer_col1_subtitle ?? '' },
        col2: { title: row.footer_col2_title ?? '', subtitle: row.footer_col2_subtitle ?? '' },
        col3: { title: row.footer_col3_title ?? '', subtitle: row.footer_col3_subtitle ?? '' },
        col4: { title: row.footer_col4_title ?? '', subtitle: row.footer_col4_subtitle ?? '' },
      }
    };
  }

  private getCheckoutFormValue(): CheckoutFormShape {
    const raw = this.form.getRawValue() as any;

    return {
      cartTitle: raw.cartTitle || '',
      itemListTitle: raw.itemListTitle || '',
      checkoutSideTitle: raw.checkoutSideTitle || '',
      shippingEnabled: !!raw.shippingEnabled,
      footerEnabled: !!raw.footerEnabled,
      footerCols: {
        col1: {
          title: raw.footerCols?.col1?.title || '',
          subtitle: raw.footerCols?.col1?.subtitle || '',
        },
        col2: {
          title: raw.footerCols?.col2?.title || '',
          subtitle: raw.footerCols?.col2?.subtitle || '',
        },
        col3: {
          title: raw.footerCols?.col3?.title || '',
          subtitle: raw.footerCols?.col3?.subtitle || '',
        },
        col4: {
          title: raw.footerCols?.col4?.title || '',
          subtitle: raw.footerCols?.col4?.subtitle || '',
        },
      },
    };
  }

  private buildFallbackEnglishTranslation(source?: CheckoutFormShape): CheckoutTranslationValue {
    const base = source ?? this.resolveCurrentSpanishSource();

    return {
      cartTitle: createTranslationField(base.cartTitle || ''),
      itemListTitle: createTranslationField(base.itemListTitle || ''),
      checkoutSideTitle: createTranslationField(base.checkoutSideTitle || ''),
      footerCols: {
        col1: {
          title: createTranslationField(base.footerCols.col1.title || ''),
          subtitle: createTranslationField(base.footerCols.col1.subtitle || ''),
        },
        col2: {
          title: createTranslationField(base.footerCols.col2.title || ''),
          subtitle: createTranslationField(base.footerCols.col2.subtitle || ''),
        },
        col3: {
          title: createTranslationField(base.footerCols.col3.title || ''),
          subtitle: createTranslationField(base.footerCols.col3.subtitle || ''),
        },
        col4: {
          title: createTranslationField(base.footerCols.col4.title || ''),
          subtitle: createTranslationField(base.footerCols.col4.subtitle || ''),
        },
      },
    };
  }

  private resolveCurrentSpanishSource(): CheckoutFormShape {
    if (!this.form) {
      return CHECKOUT_DEFAULTS;
    }

    if (this.form.get('useDefaults')?.value) {
      return CHECKOUT_DEFAULTS;
    }

    return this.getCheckoutFormValue();
  }

  private parseTranslatedContent(rawTranslatedContent: unknown): CheckoutTranslatedContentPayload | null {
    if (!rawTranslatedContent) {
      return null;
    }

    if (typeof rawTranslatedContent === 'string') {
      try {
        return JSON.parse(rawTranslatedContent);
      } catch (error) {
        console.error('Invalid checkout translatedContent JSON payload:', error);
        return null;
      }
    }

    return typeof rawTranslatedContent === 'object'
      ? rawTranslatedContent as CheckoutTranslatedContentPayload
      : null;
  }

  private wrapTranslatedField(field: any, fallbackValue: string): CheckoutTranslationField {
    if (typeof field === 'object' && field !== null && 'value' in field) {
      return {
        value: field.value ?? fallbackValue,
        manual: false,
      };
    }

    return {
      value: field ?? fallbackValue,
      manual: false,
    };
  }

  private initializeTranslationState(rawTranslatedContent: unknown): void {
    const fallback = this.buildFallbackEnglishTranslation();
    const parsed = this.parseTranslatedContent(rawTranslatedContent);
    const rawEnglish: any = parsed?.en;

    if (!rawEnglish || typeof rawEnglish !== 'object') {
      this.englishTranslation = cloneCheckoutTranslationValue(fallback);
      this.initialEnglishTranslation = cloneCheckoutTranslationValue(fallback);
      this.persistedEnglishFieldAvailability = createCheckoutFlagRecord(false);
      this.englishFieldEdited = createCheckoutFlagRecord(false);
      return;
    }

    const normalized: CheckoutTranslationValue = {
      cartTitle: this.wrapTranslatedField(rawEnglish.cartTitle, fallback.cartTitle.value),
      itemListTitle: this.wrapTranslatedField(rawEnglish.itemListTitle, fallback.itemListTitle.value),
      checkoutSideTitle: this.wrapTranslatedField(rawEnglish.checkoutSideTitle, fallback.checkoutSideTitle.value),
      footerCols: {
        col1: {
          title: this.wrapTranslatedField(rawEnglish.footerCols?.col1?.title, fallback.footerCols.col1.title.value),
          subtitle: this.wrapTranslatedField(rawEnglish.footerCols?.col1?.subtitle, fallback.footerCols.col1.subtitle.value),
        },
        col2: {
          title: this.wrapTranslatedField(rawEnglish.footerCols?.col2?.title, fallback.footerCols.col2.title.value),
          subtitle: this.wrapTranslatedField(rawEnglish.footerCols?.col2?.subtitle, fallback.footerCols.col2.subtitle.value),
        },
        col3: {
          title: this.wrapTranslatedField(rawEnglish.footerCols?.col3?.title, fallback.footerCols.col3.title.value),
          subtitle: this.wrapTranslatedField(rawEnglish.footerCols?.col3?.subtitle, fallback.footerCols.col3.subtitle.value),
        },
        col4: {
          title: this.wrapTranslatedField(rawEnglish.footerCols?.col4?.title, fallback.footerCols.col4.title.value),
          subtitle: this.wrapTranslatedField(rawEnglish.footerCols?.col4?.subtitle, fallback.footerCols.col4.subtitle.value),
        },
      },
    };

    this.englishTranslation = cloneCheckoutTranslationValue(normalized);
    this.initialEnglishTranslation = cloneCheckoutTranslationValue(normalized);
    this.persistedEnglishFieldAvailability = this.buildPersistedAvailabilityMap(normalized);
    this.englishFieldEdited = createCheckoutFlagRecord(false);
  }

  private buildPersistedAvailabilityMap(value: CheckoutTranslationValue): Record<CheckoutTranslationFieldKey, boolean> {
    const flags = createCheckoutFlagRecord(false);

    CHECKOUT_TRANSLATION_FIELD_KEYS.forEach((field) => {
      flags[field] = this.hasMeaningfulText(this.getTranslationFieldValue(value, field).value);
    });

    return flags;
  }

  private getTranslationFieldValue(source: CheckoutTranslationValue, field: CheckoutTranslationFieldKey): CheckoutTranslationField {
    switch (field) {
      case 'cartTitle':
        return source.cartTitle;
      case 'itemListTitle':
        return source.itemListTitle;
      case 'checkoutSideTitle':
        return source.checkoutSideTitle;
      case 'footerCols.col1.title':
        return source.footerCols.col1.title;
      case 'footerCols.col1.subtitle':
        return source.footerCols.col1.subtitle;
      case 'footerCols.col2.title':
        return source.footerCols.col2.title;
      case 'footerCols.col2.subtitle':
        return source.footerCols.col2.subtitle;
      case 'footerCols.col3.title':
        return source.footerCols.col3.title;
      case 'footerCols.col3.subtitle':
        return source.footerCols.col3.subtitle;
      case 'footerCols.col4.title':
        return source.footerCols.col4.title;
      case 'footerCols.col4.subtitle':
        return source.footerCols.col4.subtitle;
    }
  }

  private updateTranslationFieldValue(
    source: CheckoutTranslationValue,
    field: CheckoutTranslationFieldKey,
    nextValue: CheckoutTranslationField
  ): CheckoutTranslationValue {
    const cloned = cloneCheckoutTranslationValue(source);
    const value = cloneTranslationField(nextValue);

    switch (field) {
      case 'cartTitle':
        cloned.cartTitle = value;
        return cloned;
      case 'itemListTitle':
        cloned.itemListTitle = value;
        return cloned;
      case 'checkoutSideTitle':
        cloned.checkoutSideTitle = value;
        return cloned;
      case 'footerCols.col1.title':
        cloned.footerCols.col1.title = value;
        return cloned;
      case 'footerCols.col1.subtitle':
        cloned.footerCols.col1.subtitle = value;
        return cloned;
      case 'footerCols.col2.title':
        cloned.footerCols.col2.title = value;
        return cloned;
      case 'footerCols.col2.subtitle':
        cloned.footerCols.col2.subtitle = value;
        return cloned;
      case 'footerCols.col3.title':
        cloned.footerCols.col3.title = value;
        return cloned;
      case 'footerCols.col3.subtitle':
        cloned.footerCols.col3.subtitle = value;
        return cloned;
      case 'footerCols.col4.title':
        cloned.footerCols.col4.title = value;
        return cloned;
      case 'footerCols.col4.subtitle':
        cloned.footerCols.col4.subtitle = value;
        return cloned;
    }
  }

  private hasMeaningfulText(value: string | null | undefined): boolean {
    if (value == null) {
      return false;
    }

    return String(value).trim().length > 0;
  }

  private shouldKeepManualValue(field: CheckoutTranslationFieldKey): boolean {
    return this.getTranslationFieldValue(this.englishTranslation, field).manual === true
      && this.englishFieldEdited[field] === true;
  }

  private getAutomaticEnglishFieldValue(field: CheckoutTranslationFieldKey): string {
    if (this.persistedEnglishFieldAvailability[field]) {
      return this.getTranslationFieldValue(this.initialEnglishTranslation, field).value || '';
    }

    return this.getTranslationFieldValue(this.buildFallbackEnglishTranslation(), field).value || '';
  }

  private syncEnglishTranslationFallback(): void {
    const fallback = this.buildFallbackEnglishTranslation();
    let nextEnglishTranslation = cloneCheckoutTranslationValue(this.englishTranslation);
    let nextInitialEnglishTranslation = cloneCheckoutTranslationValue(this.initialEnglishTranslation);

    CHECKOUT_TRANSLATION_FIELD_KEYS.forEach((field) => {
      const currentField = this.getTranslationFieldValue(this.englishTranslation, field);
      const initialField = this.getTranslationFieldValue(this.initialEnglishTranslation, field);
      const fallbackField = this.getTranslationFieldValue(fallback, field);
      const keepAutomaticField = this.persistedEnglishFieldAvailability[field] === true && currentField.manual !== true;

      if (!this.shouldKeepManualValue(field) && !keepAutomaticField) {
        nextEnglishTranslation = this.updateTranslationFieldValue(nextEnglishTranslation, field, fallbackField);
      }

      if (initialField.manual !== true && this.persistedEnglishFieldAvailability[field] !== true) {
        nextInitialEnglishTranslation = this.updateTranslationFieldValue(nextInitialEnglishTranslation, field, fallbackField);
      }
    });

    this.englishTranslation = nextEnglishTranslation;
    this.initialEnglishTranslation = nextInitialEnglishTranslation;
  }

  private buildApiPayloadFromForm(): any {
    const value = this.getCheckoutFormValue();

    return {
      cartTitle: value.cartTitle,
      itemListTitle: value.itemListTitle,
      checkoutSideTitle: value.checkoutSideTitle,
      shippingEnabled: value.shippingEnabled,
      footerEnabled: value.footerEnabled,
      footerCols: {
        col1: { ...value.footerCols.col1 },
        col2: { ...value.footerCols.col2 },
        col3: { ...value.footerCols.col3 },
        col4: { ...value.footerCols.col4 },
      },
      useDefaults: 0,
      translatedContent: this.resolveTranslatedContentPayload(),
    };
  }

  private resolveTranslatedContentPayload(): string {
    const buildPayloadField = (field: CheckoutTranslationFieldKey): CheckoutTranslationField => {
      const currentField = this.getTranslationFieldValue(this.englishTranslation, field);
      const isManual = currentField.manual === true && this.englishFieldEdited[field] === true;

      return {
        manual: isManual,
        value: isManual ? (currentField.value || '') : '',
      };
    };

    const payload: CheckoutTranslatedContentPayload = {
      en: {
        cartTitle: buildPayloadField('cartTitle'),
        itemListTitle: buildPayloadField('itemListTitle'),
        checkoutSideTitle: buildPayloadField('checkoutSideTitle'),
        footerCols: {
          col1: {
            title: buildPayloadField('footerCols.col1.title'),
            subtitle: buildPayloadField('footerCols.col1.subtitle'),
          },
          col2: {
            title: buildPayloadField('footerCols.col2.title'),
            subtitle: buildPayloadField('footerCols.col2.subtitle'),
          },
          col3: {
            title: buildPayloadField('footerCols.col3.title'),
            subtitle: buildPayloadField('footerCols.col3.subtitle'),
          },
          col4: {
            title: buildPayloadField('footerCols.col4.title'),
            subtitle: buildPayloadField('footerCols.col4.subtitle'),
          },
        },
      }
    };

    return JSON.stringify(payload);
  }

  private commitTranslationStateAfterSave(): void {
    const nextAvailability = { ...this.persistedEnglishFieldAvailability };

    CHECKOUT_TRANSLATION_FIELD_KEYS.forEach((field) => {
      const currentField = this.getTranslationFieldValue(this.englishTranslation, field);

      if (currentField.manual === true && this.hasMeaningfulText(currentField.value)) {
        nextAvailability[field] = true;
      }
    });

    this.initialEnglishTranslation = cloneCheckoutTranslationValue(this.englishTranslation);
    this.persistedEnglishFieldAvailability = nextAvailability;
    this.englishFieldEdited = createCheckoutFlagRecord(false);
  }

  selectLanguage(language: 'es' | 'en'): void {
    this.selectedLanguage = language;
  }

  getFooterFieldPath(column: FooterColKey, field: FooterFieldKey): CheckoutTranslationFieldKey {
    return `footerCols.${column}.${field}` as CheckoutTranslationFieldKey;
  }

  getFooterColumnLabel(column: FooterColKey): string {
    return `Col ${column.replace('col', '')}`;
  }

  getEnglishFieldValue(field: CheckoutTranslationFieldKey): string {
    return this.getTranslationFieldValue(this.englishTranslation, field).value || '';
  }

  onManualFieldToggle(field: CheckoutTranslationFieldKey, event: Event): void {
    const enabled = (event.target as HTMLInputElement).checked;
    const currentField = this.getTranslationFieldValue(this.englishTranslation, field);
    const automaticValue = this.getAutomaticEnglishFieldValue(field);

    if (enabled && !this.hasMeaningfulText(currentField.value)) {
      this.englishTranslation = this.updateTranslationFieldValue(this.englishTranslation, field, {
        value: automaticValue,
        manual: true,
      });
      this.englishFieldEdited[field] = false;
    } else {
      this.englishTranslation = this.updateTranslationFieldValue(this.englishTranslation, field, {
        value: enabled ? currentField.value : automaticValue,
        manual: enabled,
      });

      if (!enabled) {
        this.englishFieldEdited[field] = false;
      }
    }

    if (enabled) {
      this.selectedLanguage = 'en';
    }
  }

  updateEnglishField(field: CheckoutTranslationFieldKey, value: string): void {
    const currentField = this.getTranslationFieldValue(this.englishTranslation, field);

    this.englishTranslation = this.updateTranslationFieldValue(this.englishTranslation, field, {
      ...currentField,
      value: value || '',
    });
    this.englishFieldEdited[field] = true;
  }

  isEnglishFieldReadonly(field: CheckoutTranslationFieldKey): boolean {
    return this.getTranslationFieldValue(this.englishTranslation, field).manual !== true;
  }

  hasEnglishFieldPreview(field: CheckoutTranslationFieldKey): boolean {
    return this.hasMeaningfulText(this.getTranslationFieldValue(this.englishTranslation, field).value);
  }

  isEnglishFieldManual(field: CheckoutTranslationFieldKey): boolean {
    return this.getTranslationFieldValue(this.englishTranslation, field).manual === true;
  }

  private async refreshCheckoutTemplateState(): Promise<void> {
    const res: any = await this.lambdaService.getCheckoutTemplate(this.themingService.shopId);
    const row = Array.isArray(res?.data) ? res.data[0] : res?.data;

    if (!row) {
      this.dbSnapshot = null;
      this.form.patchValue({ ...CHECKOUT_DEFAULTS, useDefaults: true }, { emitEvent: false });
      this.form.disable({ emitEvent: false });
      this.form.get('useDefaults')?.enable({ emitEvent: false });
      this.initializeTranslationState(null);
      this.syncEnglishTranslationFallback();
      return;
    }

    this.dbSnapshot = this.dbRowToForm(row);
    const useDefaults = row.use_defaults === 1;

    if (useDefaults) {
      this.form.patchValue({ ...CHECKOUT_DEFAULTS, useDefaults: true }, { emitEvent: false });
      this.form.disable({ emitEvent: false });
      this.form.get('useDefaults')?.enable({ emitEvent: false });
    } else {
      this.form.patchValue({ ...this.dbSnapshot, useDefaults: false }, { emitEvent: false });
      this.form.enable({ emitEvent: false });
    }

    this.initializeTranslationState(row.translatedContent ?? null);
    this.syncEnglishTranslationFallback();
  }

  saveCheckoutTemplate(): void {
    const useDefaults = !!this.form.get('useDefaults')?.value;

    if (useDefaults) {
      const body = { useDefaults: 1 };
      this.loading = true;
      this.lambdaService.upsertCheckoutTemplate(this.themingService.shopId, body)
        .then(async (res: any) => {
          const ok = res?.status === 200 || res?.statusCode === 200 || res === true;
          if (ok) {
            try {
              await this.refreshCheckoutTemplateState();
            } catch (refreshError) {
              console.error('Error refreshing checkout template after save', refreshError);
            }
            this.openModal('#checkoutSuccessModal');
          } else {
            this.openModal('#checkoutErrorModal');
          }
        })
        .catch(err => {
          console.error('POST error', err);
          this.openModal('#checkoutErrorModal');
        })
        .finally(() => this.loading = false);
      return;
    }

    this.showErrors = true;
    if (this.f.cartTitle.invalid || this.f.itemListTitle.invalid || this.f.checkoutSideTitle.invalid) {
      this.openModal('#checkoutRequiredModal');
      return;
    }

    const body = this.buildApiPayloadFromForm();
    this.loading = true;
    this.lambdaService.upsertCheckoutTemplate(this.themingService.shopId, body)
      .then(async (res: any) => {
        const ok = res?.status === 200 || res?.statusCode === 200 || res === true;
        if (ok) {
          try {
            await this.refreshCheckoutTemplateState();
          } catch (refreshError) {
            console.error('Error refreshing checkout template after save', refreshError);
            this.dbSnapshot = {
              cartTitle: body.cartTitle,
              itemListTitle: body.itemListTitle,
              checkoutSideTitle: body.checkoutSideTitle,
              shippingEnabled: body.shippingEnabled,
              footerEnabled: body.footerEnabled,
              footerCols: body.footerCols
            };
            this.commitTranslationStateAfterSave();
          }
          this.openModal('#checkoutSuccessModal');
        } else {
          this.openModal('#checkoutErrorModal');
        }
      })
      .catch(err => {
        console.error('POST error', err);
        this.openModal('#checkoutErrorModal');
      })
      .finally(() => this.loading = false);
  }

  closeModal() {
    this.hideModal('#checkoutSuccessModal');
    this.hideModal('#checkoutRequiredModal');
    this.hideModal('#checkoutErrorModal');
  }

  private openModal(selector: string) {
    if (typeof $ !== 'undefined') $(selector).modal('show');
  }

  private hideModal(selector: string) {
    if (typeof $ !== 'undefined') $(selector).modal('hide');
  }
}
